import { isConnectEnvironmentAdded } from "./connectEnvironments";

/** The part of a listed Cloud Workspace that registering it needs. */
export interface ListedCloudWorkspace {
  readonly id: string;
  readonly label: string;
}

/**
 * What to do with the account's listing on this device: seed what it uses on its first listing,
 * register each added workspace once (again only when its label changed), and let go of saved
 * ones that are no longer reachable, once per departure. The local Environment is never touched —
 * it is this machine, not a Cloud Workspace.
 */
export function planCloudWorkspaceRegistration<Workspace extends ListedCloudWorkspace>(input: {
  readonly listed: ReadonlyArray<Workspace>;
  /** Network introductions remain reachable independently of the Cloud Workspace listing. */
  readonly remembered?: ReadonlyArray<ListedCloudWorkspace>;
  /** Undefined until this device first reads the account's listing. */
  readonly added: ReadonlyArray<string> | undefined;
  /** Every Environment the catalog holds, and whether it is the local one. */
  readonly catalog: ReadonlyMap<string, { readonly local: boolean }>;
  readonly registered: ReadonlyMap<string, string>;
  readonly released: ReadonlySet<string>;
}) {
  const remembered = input.remembered ?? [];
  const listed = [
    ...new Map([
      ...remembered.map((machine) => [machine.id, machine] as const),
      ...input.listed.map((workspace) => [workspace.id, workspace] as const),
    ]).values(),
  ];
  const added =
    input.added === undefined
      ? undefined
      : [...input.added, ...remembered.map((machine) => machine.id)];
  const local = (id: string) => input.catalog.get(id)?.local === true;
  const seed =
    input.added === undefined
      ? listed.filter((workspace) => !local(workspace.id)).map((workspace) => workspace.id)
      : null;
  const reachable = new Set(
    listed
      .filter((workspace) => isConnectEnvironmentAdded(added, workspace.id))
      .map((workspace) => workspace.id),
  );
  const register = listed.filter(
    (workspace) =>
      reachable.has(workspace.id) &&
      !local(workspace.id) &&
      // Re-registering an unchanged workspace would replace a live connection's entry.
      input.registered.get(workspace.id) !== workspace.label,
  );
  const release = [...input.catalog]
    .filter(([id, entry]) => !entry.local && !reachable.has(id) && !input.released.has(id))
    .map(([id]) => id);
  return { seed, register, release };
}
